import { useMutation, useTRPC } from "@luckymenu/api-client/react";
import type { Category } from "@luckymenu/core";
import { useCallback, useMemo, useState } from "react";
import { withCategory, withoutCategory } from "../menu/cache";
import {
	type CategoryDraft,
	categoryDraftToInput,
	categoryToDraft,
	newCategoryDraft,
} from "../menu/category-form";
import type { FormErrors } from "../menu/form-errors";
import { type MenuProblem, menuProblemOf, problemCount } from "../menu/problems";
import { useFileUpload } from "./use-file-upload";
import { useMenu, useMenuCache } from "./use-menu";
import { useStoredDraft } from "./use-stored-draft";

const newBase = (draft: CategoryDraft) => JSON.stringify({ ...draft, id: "" });
const editBase = (draft: CategoryDraft) => JSON.stringify(draft);

/**
 * The category form, shared by both apps: `categoryId` to edit, or null for
 * a new category. Same shape as `useItemForm`. Deleting a category that
 * still has items is refused with `categoryNotEmpty` and `problemCount`.
 */
export function useCategoryForm({ categoryId }: { categoryId: string | null }) {
	const trpc = useTRPC();
	const menu = useMenu();
	const cache = useMenuCache();
	const photo = useFileUpload("category_image");
	const [errors, setErrors] = useState<FormErrors>({});
	const [problem, setProblem] = useState<{ problem: MenuProblem; count: number | null } | null>(
		null,
	);

	const original: Category | null = useMemo(
		() => (categoryId ? (menu.data?.categories.find((c) => c.id === categoryId) ?? null) : null),
		[menu.data, categoryId],
	);
	const loaded = menu.data !== undefined;
	const initial = useMemo(() => {
		if (categoryId) return original ? categoryToDraft(original) : null;
		return loaded ? newCategoryDraft() : null;
	}, [categoryId, original, loaded]);
	const stored = useStoredDraft(
		categoryId ? `category:${categoryId}` : "category:new",
		initial,
		categoryId ? editBase : newBase,
	);
	const draft = stored.draft;
	const itemCount = useMemo(
		() =>
			categoryId ? (menu.data?.items.filter((i) => i.categoryId === categoryId).length ?? 0) : 0,
		[menu.data, categoryId],
	);

	const edit = useCallback(
		(change: (d: CategoryDraft) => CategoryDraft) => {
			setProblem(null);
			stored.update(change);
		},
		[stored.update],
	);
	const save = useMutation(trpc.staff.catalog.upsertCategory.mutationOptions());
	const remove = useMutation(trpc.staff.catalog.deleteCategory.mutationOptions());

	return {
		status: menu.isPending
			? ("loading" as const)
			: categoryId && !original
				? ("notFound" as const)
				: !draft
					? ("loading" as const)
					: ("ready" as const),
		isNew: categoryId === null,
		original,
		/** Items in the category; it can be deleted only when this is 0. */
		itemCount,
		draft,
		restored: stored.restored,
		dirty: stored.dirty,
		errors,
		/** A refusal, with the item count for `categoryNotEmpty`. */
		problem,
		saving: save.isPending,
		deleting: remove.isPending,

		set: <K extends keyof CategoryDraft>(field: K, value: CategoryDraft[K]) => {
			// The time problem sits on `availableTo`; any time change clears it.
			const key =
				field === "timed" || field === "availableFrom" ? "availableTo" : (field as string);
			setErrors((e) => {
				if (!(key in e)) return e;
				const next = { ...e };
				delete next[key];
				return next;
			});
			edit((d) => ({ ...d, [field]: value }));
		},
		photo: photo.state,
		pickPhoto: async () => {
			const info = await photo.pickAndUpload();
			if (info) edit((d) => ({ ...d, image: { id: info.id, blurhash: info.blurhash } }));
		},
		removePhoto: () => {
			photo.reset();
			edit((d) => ({ ...d, image: null }));
		},
		discard: () => {
			setErrors({});
			setProblem(null);
			photo.reset();
			stored.reset();
		},

		submit: async (): Promise<Category | null> => {
			if (!draft) return null;
			const result = categoryDraftToInput(draft, original);
			if (!result.ok) {
				setErrors(result.errors);
				return null;
			}
			setErrors({});
			setProblem(null);
			try {
				const category = await save.mutateAsync(result.input);
				cache.update((m) => withCategory(m, category));
				if (categoryId) stored.reset(categoryToDraft(category));
				else stored.forget();
				return category;
			} catch (error) {
				setProblem({ problem: menuProblemOf(error), count: null });
				return null;
			}
		},

		delete: async (): Promise<boolean> => {
			if (!categoryId) return false;
			try {
				await remove.mutateAsync({ id: categoryId });
				stored.forget();
				cache.update((m) => withoutCategory(m, categoryId));
				return true;
			} catch (error) {
				setProblem({ problem: menuProblemOf(error), count: problemCount(error) });
				return false;
			}
		},
	};
}

export type CategoryForm = ReturnType<typeof useCategoryForm>;
